import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Define interfaces for Division and the API response
export interface Division { // Exported for use in other files
  id: string;
  name: string;
  slug: string; // Added slug
  // Removed description
  createdAt?: string;
  updatedAt?: string;
}

interface DivisionsListResponse {
  divisions: Division[];
}

const backendApi = config.backendApi;

// The centralized fetch function - removed _organizationId as it's not directly used in the API path
const fetchDivisions = async (): Promise<Division[]> => {
  const response = await axios.get<DivisionsListResponse>(`${backendApi}/api/divisions`, { withCredentials: true });
  // Defensive: tolerate an unexpected response shape instead of handing a
  // non-array to callers that `.map`/`.filter` it.
  return Array.isArray(response.data.divisions) ? response.data.divisions : [];
};

// The custom hook
export function useDivisions(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<Division[]>({
    queryKey: ['divisions', organizationId], // Include organizationId in queryKey if divisions are organization-specific
    queryFn: () => {
      if (!organizationId) {
        // This case should ideally be prevented by 'enabled' but provides type safety
        throw new Error("organizationId is required to fetch divisions.");
      }
      return fetchDivisions(); // Call fetcher without organizationId parameter
    },
    // Enable the query only if organizationId is available
    enabled: !!organizationId && contextReady,
  });
}
